import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  MapPin, Clock, Users, Video, ExternalLink, ArrowRight,
  Download, Loader2, Plus, CalendarDays
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { cn } from "../../lib/utils";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const statusColors = {
  scheduled: "bg-blue-500",
  confirmed: "bg-blue-500",
  in_progress: "bg-amber-500",
  completed: "bg-green-500",
  cancelled: "bg-red-400",
  requested: "bg-purple-400",
};

const statusLabels = {
  scheduled: "Scheduled",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
  requested: "Pending Approval",
};

const CalendarPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingIcs, setDownloadingIcs] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const base = user?.role === "super_admin" ? "/admin" : user?.role === "tenant_admin" ? "/tenant" : "/learner";

  useEffect(() => { fetchEvents(); }, [month, year]); // eslint-disable-line

  const fetchEvents = async () => {
    try {
      const res = await api.get(`/sessions/calendar/events?month=${month + 1}&year=${year}`);
      setEvents(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();
    const days = [];
    for (let i = firstDay - 1; i >= 0; i--) days.push({ day: prevMonthDays - i, currentMonth: false, date: "" });
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      days.push({ day: d, currentMonth: true, date: dateStr });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) days.push({ day: i, currentMonth: false, date: "" });
    return days;
  }, [year, month]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach(e => { if (!map[e.date]) map[e.date] = []; map[e.date].push(e); });
    return map;
  }, [events]);

  const todayStr = new Date().toISOString().split("T")[0];
  const selectedEvents = selectedDate ? (eventsByDate[selectedDate] || []) : [];
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToSession = (sessionId) => navigate(`${base}/sessions/${sessionId}`);
  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";

  const handleDownloadICS = async (sessionId, title, e) => {
    if (e) e.stopPropagation();
    setDownloadingIcs(sessionId);
    try {
      const res = await api.get(`/sessions/${sessionId}/ics`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title?.replace(/\s+/g, "_") || "session"}.ics`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Calendar file downloaded");
    } catch (err) { toast.error("Failed to download"); }
    finally { setDownloadingIcs(null); }
  };

  const totalEvents = events.length;
  const upcoming = events.filter(e => e.date >= todayStr && e.status !== "cancelled").length;
  const completed = events.filter(e => e.status === "completed").length;
  const requested = events.filter(e => e.status === "requested").length;

  return (
    <AdminLayout title="ILT Training Calendar" variant={variant}>
      <div className="space-y-5" data-testid="ilt-calendar-page">
        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><CalendarIcon className="w-4.5 h-4.5 text-blue-600" /></div>
            <div><p className="text-lg font-bold leading-none">{totalEvents}</p><p className="text-[10px] text-muted-foreground">This Month</p></div>
          </CardContent></Card>
          <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center"><Clock className="w-4.5 h-4.5 text-amber-600" /></div>
            <div><p className="text-lg font-bold leading-none">{upcoming}</p><p className="text-[10px] text-muted-foreground">Upcoming</p></div>
          </CardContent></Card>
          <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center"><Users className="w-4.5 h-4.5 text-green-600" /></div>
            <div><p className="text-lg font-bold leading-none">{completed}</p><p className="text-[10px] text-muted-foreground">Completed</p></div>
          </CardContent></Card>
          {requested > 0 && (
            <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center"><CalendarDays className="w-4.5 h-4.5 text-purple-600" /></div>
              <div><p className="text-lg font-bold leading-none">{requested}</p><p className="text-[10px] text-muted-foreground">Pending Approval</p></div>
            </CardContent></Card>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Calendar Grid */}
          <Card className="lg:col-span-2 border shadow-none">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-5">
                <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="prev-month-btn"><ChevronLeft className="w-5 h-5" /></Button>
                <h3 className="text-base font-bold">{MONTHS[month]} {year}</h3>
                <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="next-month-btn"><ChevronRight className="w-5 h-5" /></Button>
              </div>
              <div className="grid grid-cols-7 gap-px mb-1">
                {DAYS.map(d => <div key={d} className="text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider py-2">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-px">
                {calendarDays.map((cell, i) => {
                  const dayEvents = cell.date ? (eventsByDate[cell.date] || []) : [];
                  const isToday = cell.date === todayStr;
                  const isSelected = cell.date === selectedDate;
                  return (
                    <button
                      key={i}
                      onClick={() => cell.currentMonth && setSelectedDate(cell.date)}
                      className={cn(
                        "relative h-20 p-1.5 text-left rounded-md transition-all border border-transparent",
                        cell.currentMonth ? "hover:bg-slate-50 hover:border-slate-200 cursor-pointer" : "opacity-30 cursor-default",
                        isToday && "bg-blue-50/60 border-blue-200",
                        isSelected && "bg-blue-50 border-blue-300 ring-1 ring-blue-300"
                      )}
                      data-testid={cell.date ? `cal-day-${cell.date}` : undefined}
                    >
                      <span className={cn("text-xs font-medium", isToday && "text-blue-600 font-bold")}>{cell.day}</span>
                      {dayEvents.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {dayEvents.slice(0, 2).map((ev, j) => (
                            <div key={j} className={cn("h-1.5 rounded-full", statusColors[ev.status] || "bg-blue-500")} title={ev.title} />
                          ))}
                          {dayEvents.length > 2 && <span className="text-[9px] text-muted-foreground">+{dayEvents.length - 2}</span>}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {/* Legend */}
              <div className="mt-4 flex items-center gap-4 flex-wrap">
                {Object.entries(statusColors).map(([key, color]) => (
                  <div key={key} className="flex items-center gap-1.5">
                    <div className={cn("w-2.5 h-2.5 rounded-full", color)} />
                    <span className="text-[10px] text-muted-foreground capitalize">{statusLabels[key] || key}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Selected Day Panel */}
          <Card className="border shadow-none">
            <CardContent className="p-5">
              <h3 className="font-semibold text-sm mb-4">
                {selectedDate
                  ? new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })
                  : "Select a date"}
              </h3>
              {selectedEvents.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{selectedDate ? "No sessions on this day" : "Click a date to view sessions"}</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {selectedEvents.map(ev => (
                    <motion.div
                      key={ev.id}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="p-3 rounded-lg border hover:border-primary/30 hover:bg-primary/5 transition-all cursor-pointer group"
                      onClick={() => goToSession(ev.id)}
                      data-testid={`event-card-${ev.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={cn("w-2 h-2 rounded-full shrink-0", statusColors[ev.status])} />
                          <h4 className="font-medium text-sm leading-tight">{ev.title}</h4>
                        </div>
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/30 group-hover:text-primary transition-colors shrink-0 mt-0.5" />
                      </div>
                      <div className="space-y-1 text-xs text-muted-foreground ml-4">
                        <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" />{ev.start_time} - {ev.end_time}</div>
                        <div className="flex items-center gap-1.5">
                          {ev.location_type === "virtual" ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                          {ev.location || "TBD"}
                        </div>
                        <div className="flex items-center gap-1.5"><Users className="w-3 h-3" />{ev.enrolled_count}/{ev.capacity} enrolled</div>
                      </div>
                      <div className="flex items-center justify-between mt-2 ml-4">
                        <Badge className={cn("text-[9px] px-1.5 py-0 border", {
                          "bg-blue-50 text-blue-700 border-blue-200": ev.status === "scheduled" || ev.status === "confirmed",
                          "bg-amber-50 text-amber-700 border-amber-200": ev.status === "in_progress",
                          "bg-green-50 text-green-700 border-green-200": ev.status === "completed",
                          "bg-red-50 text-red-700 border-red-200": ev.status === "cancelled",
                          "bg-purple-50 text-purple-700 border-purple-200": ev.status === "requested",
                        })}>{statusLabels[ev.status] || ev.status}</Badge>
                        <div className="flex items-center gap-1">
                          <button
                            className="text-muted-foreground hover:text-primary transition-colors p-1"
                            onClick={(e) => handleDownloadICS(ev.id, ev.title, e)}
                            title="Download .ICS"
                            data-testid={`ics-btn-${ev.id}`}
                          >
                            {downloadingIcs === ev.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                          </button>
                          <span className="text-[10px] text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                            Details <ExternalLink className="w-2.5 h-2.5" />
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CalendarPage;
